<script setup lang="ts">
import { reactive, watch } from 'vue'
import { calibrationFromTwoPoints } from '../../core/map/coordinates'
import type { CoordinateCalibration, MapAsset, PlayerState } from '../../types/game'

const props = defineProps<{
  map: MapAsset
  player: PlayerState
  calibration: CoordinateCalibration
}>()

const emit = defineEmits<{
  save: [calibration: CoordinateCalibration]
}>()

const form = reactive({
  world1X: props.player.x,
  world1Y: props.player.y,
  pixel1X: props.calibration.pixelOriginX,
  pixel1Y: props.calibration.pixelOriginY,
  world2X: props.player.x + 100,
  world2Y: props.player.y + 100,
  pixel2X: props.calibration.pixelOriginX + 100,
  pixel2Y: props.calibration.pixelOriginY + 100
})
let error = ''

watch(
  () => props.map.biome,
  () => {
    form.world1X = props.player.x
    form.world1Y = props.player.y
    form.pixel1X = props.calibration.pixelOriginX
    form.pixel1Y = props.calibration.pixelOriginY
    form.world2X = props.player.x + 100
    form.world2Y = props.player.y + 100
    form.pixel2X = props.calibration.pixelOriginX + 100
    form.pixel2Y = props.calibration.pixelOriginY + 100
  }
)

function save() {
  try {
    error = ''
    emit('save', calibrationFromTwoPoints(
      props.map.biome,
      { x: form.world1X, y: form.world1Y },
      { x: form.pixel1X, y: form.pixel1Y },
      { x: form.world2X, y: form.world2Y },
      { x: form.pixel2X, y: form.pixel2Y }
    ))
  } catch (cause) {
    error = cause instanceof Error ? cause.message : '校准参数无效。'
  }
}
</script>

<template>
  <details class="calibration-panel">
    <summary>
      坐标校准
      <span :class="calibration.status">{{ calibration.status }}</span>
    </summary>
    <p>
      用两个已知点建立世界坐标到 PNG 像素的线性变换。未校准前不会绘制伪精确标记。
    </p>
    <div class="calibration-grid">
      <fieldset>
        <legend>校准点 A</legend>
        <label>世界 X <input v-model.number="form.world1X" type="number" /></label>
        <label>世界 Y <input v-model.number="form.world1Y" type="number" /></label>
        <label>像素 X <input v-model.number="form.pixel1X" type="number" /></label>
        <label>像素 Y <input v-model.number="form.pixel1Y" type="number" /></label>
      </fieldset>
      <fieldset>
        <legend>校准点 B</legend>
        <label>世界 X <input v-model.number="form.world2X" type="number" /></label>
        <label>世界 Y <input v-model.number="form.world2Y" type="number" /></label>
        <label>像素 X <input v-model.number="form.pixel2X" type="number" /></label>
        <label>像素 Y <input v-model.number="form.pixel2Y" type="number" /></label>
      </fieldset>
    </div>
    <button type="button" @click="save">应用校准</button>
    <p v-if="error" class="error">{{ error }}</p>
  </details>
</template>

<style scoped>
.calibration-panel {
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: var(--panel);
}

summary {
  cursor: pointer;
  font-weight: 800;
}

summary span {
  margin-left: 8px;
  color: var(--muted);
  font-size: 12px;
}

summary span.manual {
  color: var(--accent);
}

p {
  color: var(--muted);
  line-height: 1.6;
}

.calibration-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

fieldset {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: 12px;
}

label {
  display: grid;
  gap: 5px;
  color: var(--muted);
  font-size: 12px;
}

input {
  width: 100%;
  padding: 8px;
  border: 1px solid var(--line);
  border-radius: 8px;
  color: var(--text);
  background: #0c121c;
}

button {
  margin-top: 12px;
  padding: 9px 13px;
  border: 0;
  border-radius: 10px;
  color: #102113;
  background: var(--accent);
  font-weight: 800;
  cursor: pointer;
}

.error {
  color: #ff9d9d;
}

@media (max-width: 720px) {
  .calibration-grid,
  fieldset {
    grid-template-columns: 1fr;
  }
}
</style>
